/**
 * Screener hooks — Vault-backed, account-scoped. Distinct from the legacy
 * useScreener (internal-mail), which the cleanup slice removes.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import type { MailboxScreenedSenderDto } from '@/lib/api';

const mb = queryKeys.email.mailbox;
const conn = queryKeys.email.connections;

export function useScreenedSenders(
  accountId: string | undefined,
  status: 'pending' | 'approved' | 'rejected' = 'pending',
  enabled = true,
) {
  return useQuery<MailboxScreenedSenderDto[]>({
    queryKey: mb.screenerList(accountId ?? '', status),
    queryFn: async () => (await api.listEmailMailboxScreener(accountId as string, status)).senders,
    enabled: enabled && !!accountId,
  });
}

export function useDecideSender(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      screenedId,
      decision,
    }: {
      screenedId: string;
      decision: 'approved' | 'rejected';
    }) => api.decideEmailMailboxScreener(screenedId, accountId, decision),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: mb.screener(accountId) });
      // Decisions change inbox visibility; a reject also moves the sender's mail
      // to Trash, so refresh the message lists AND the folder counts.
      qc.invalidateQueries({ queryKey: mb.messages() });
      qc.invalidateQueries({ queryKey: [...mb.all, 'folders'] });
      if (res?.trashed && res.trashed > 0) {
        toast({
          description: `Rejected — ${res.trashed} message${res.trashed === 1 ? '' : 's'} moved to Trash`,
        });
      }
    },
  });
}

export function useSetScreenerEnabled(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.setEmailMailboxScreenerEnabled(accountId, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conn.all });
      qc.invalidateQueries({ queryKey: mb.messages() });
    },
  });
}

/** Toggle the per-account "screener runs before the AI spam filter" order. */
export function useSetScreenerFirst(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (screenerFirst: boolean) =>
      api.setEmailMailboxScreenerFirst(accountId, screenerFirst),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conn.all });
    },
  });
}
